import { pick } from 'lodash'
import merge from 'lodash/merge'
import uniqueId from 'lodash/uniqueId'
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { PromptContainer } from './PromptContainer'
import {
  PromptButton,
  PromptContent,
  PromptOptions,
  PromptState,
  PromptType,
  RequiredPromptOptions,
  UsePromptReturn,
} from './types'

interface PromptContextState {
  prompts: PromptType<any, PromptState>[]
  setPrompts: React.Dispatch<
    React.SetStateAction<PromptType<any, PromptState>[]>
  >
  promptState: PromptState
  setPromptState: React.Dispatch<React.SetStateAction<PromptState>>
  globalOptions: PromptOptions
}
export const PromptContext = React.createContext<PromptContextState | null>(
  null
)

function promptOptionDefaults<S extends PromptState>(
  options: PromptOptions<S>
): RequiredPromptOptions<S> {
  const defaultButtons: [PromptButton, PromptButton] = [
    {
      value: false,
      label: 'Cancel',

      style: {
        hoverColor: 'gray5',
        cursor: 'pointer',
        background: 'gray4',
      },
    },
    {
      value: true,
      label: 'Confirm',
      style: {
        hoverColor: 'gray5',
        cursor: 'pointer',
      },
    },
  ]

  const defaultOptions: RequiredPromptOptions = {
    modalProps: {
      placement: 'center-center',
      direction: 'from-bottom',
    },

    closeOnResolve: true,
    initialState: {},

    buttons: defaultButtons,
    style: {
      background: 'white',
      padding: '1rem',
      borderRadius: '4px',
    },
  }

  return merge({}, defaultOptions, options)
}

function useLocalPromptState(
  globalOptions?: PromptOptions
): PromptContextState {
  const [prompts, setPrompts] = useState<PromptType<any>[]>([])
  const [promptState, setPromptState] = useState<PromptState>({})

  return {
    prompts,
    setPrompts,
    promptState,
    setPromptState,
    globalOptions: globalOptions ?? {},
  }
}

export function usePromptState<S extends PromptState = PromptState>(
  id: string
) {
  const ctx = useContext(PromptContext)
  if (ctx === null)
    throw new Error('usePromptState can only be used within a PromptProvider.')

  if (!Object.prototype.hasOwnProperty.call(ctx.promptState, id))
    throw new Error('Prompt state does not exist for this prompt')

  return ctx.promptState[id] as S
}

export function usePrompt<S extends PromptState = PromptState>(
  defaultOptions: PromptOptions<S> = {}
) {
  const ctx = useContext(PromptContext)
  if (ctx === null)
    throw new Error('usePrompt can only be used within a PromptProvider.')

  const promptIds = useRef<string[]>([])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cleanupPrompts = useCallback(ctx.setPrompts, [])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cleanupState = useCallback(ctx.setPromptState, [])

  useEffect(() => {
    promptIds.current = ctx.prompts.map(p => p.id)
  }, [ctx])

  useEffect(
    () => () => {
      cleanupPrompts(ps => ps.filter(p => !promptIds.current.includes(p.id)))
      cleanupState(ps => pick(ps, promptIds.current))
    },
    [cleanupPrompts, cleanupState]
  )

  return function <T = boolean, S extends PromptState = PromptState>(
    content: PromptContent<T>,
    options: PromptOptions<S> = {}
  ): UsePromptReturn<T, S> {
    const mergedOptions = promptOptionDefaults(
      merge({}, ctx.globalOptions, defaultOptions, options)
    )

    return new Promise((resolve, reject) => {
      const id = uniqueId('prompt')

      ctx.setPromptState(v => ({
        ...v,
        [id]: mergedOptions.initialState,
      }))

      const update = (value: Partial<S>) => {
        ctx.setPromptState(v => {
          return {
            ...v,
            [id]: {
              ...v[id],
              ...value,
            },
          }
        })
      }

      const closePrompt = () =>
        ctx.setPrompts(ps =>
          ps.map(p =>
            p.id !== id
              ? p
              : {
                  ...p,
                  open: false,
                }
          )
        )

      const newPrompt: PromptType<T, S> = {
        id,
        content,
        options: mergedOptions,
        open: true,
        resolve(data) {
          resolve({
            data,
            close: closePrompt,
            update,
          })

          if (mergedOptions.closeOnResolve) closePrompt()
        },
        reject(reason) {
          reject(reason)

          if (mergedOptions.closeOnResolve) closePrompt()
        },
      }

      ctx.setPrompts(ps => [...ps, newPrompt])
    })
  }
}

export interface PromptProviderProps extends PropsWithChildren<object> {
  options?: PromptOptions
}

export const PromptProvider: React.FC<PromptProviderProps> = ({
  children,
  options = {},
}) => {
  const state = useLocalPromptState(options)

  return (
    <PromptContext.Provider value={state}>
      <PromptContainer prompts={state.prompts} />
      {children}
    </PromptContext.Provider>
  )
}
